import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  User, Settings, Package, Heart, MessageSquare, Bell, LogOut,
  Plus, Edit, Eye, EyeOff, Trash2, Clock, CheckCircle, AlertCircle,
  BarChart3, MapPin, Phone, Mail, Building2, Crown, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const MENU_ITEMS = [
  { id: 'dashboard', label: 'Tableau de bord', icon: BarChart3 },
  { id: 'annonces', label: 'Mes annonces', icon: Package },
  { id: 'favoris', label: 'Favoris', icon: Heart },
  { id: 'messages', label: 'Messages', icon: MessageSquare },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'parametres', label: 'Paramètres', icon: Settings },
];

export default function MonCompte() {
  const { API, user, setUser } = useContext(AppContext);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const [productsRes, subscriptionRes] = await Promise.all([
        axios.get(`${API}/products/my-products`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/subscriptions-v2/my-subscription?user_id=${user?.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null }))
      ]);

      setProducts(productsRes.data || []);
      setSubscription(subscriptionRes.data);
      
      // Calculate stats
      const prods = productsRes.data || [];
      setStats({
        total_annonces: prods.length,
        en_ligne: prods.filter(p => p.approval_status === 'approved' || p.status === 'en_ligne').length,
        en_attente: prods.filter(p => p.approval_status === 'pending' || p.status === 'en_attente').length,
        vues_total: prods.reduce((acc, p) => acc + (p.views || 0), 0),
        contacts: prods.reduce((acc, p) => acc + (p.contact_count || 0), 0),
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/');
    toast.success('Déconnexion réussie');
  };

  const getStatusBadge = (product) => {
    const status = product.status || (product.approval_status === 'approved' ? 'en_ligne' : 'en_attente');
    if (status === 'en_ligne') return { color: 'bg-green-100 text-green-800', label: 'En ligne', icon: CheckCircle };
    if (status === 'en_attente') return { color: 'bg-yellow-100 text-yellow-800', label: 'En attente', icon: Clock };
    if (status === 'brouillon') return { color: 'bg-gray-100 text-gray-800', label: 'Brouillon', icon: Edit };
    return { color: 'bg-blue-100 text-blue-800', label: status, icon: AlertCircle };
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0A2540] to-[#1a3a5c] text-white">
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center">
                <User className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">{user.name || user.email}</h1>
                <p className="text-gray-300">{user.email}</p>
                {subscription?.plan && (
                  <Badge className="mt-1 bg-[#635BFF]">
                    <Crown className="w-3 h-3 mr-1" />
                    {subscription.plan.name}
                  </Badge>
                )}
              </div>
            </div>
            <div className="mt-4 md:mt-0 flex gap-3">
              <Link to="/user/add-product">
                <Button className="bg-[#635BFF] hover:bg-[#635BFF]/90">
                  <Plus className="w-4 h-4 mr-2" />
                  Nouvelle annonce
                </Button>
              </Link>
              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10" onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Sidebar */}
          <div className="w-full md:w-64 flex-shrink-0">
            <Card>
              <CardContent className="p-2">
                {MENU_ITEMS.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      onClick={() => setActiveTab(item.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                        activeTab === item.id
                          ? 'bg-[#635BFF] text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span>{item.label}</span>
                    </button>
                  );
                })}
              </CardContent>
            </Card>

            {/* Subscription Card */}
            {subscription && (
              <Card className="mt-4">
                <CardContent className="p-4">
                  <h3 className="font-semibold text-[#0A2540] mb-2">Mon abonnement</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Plan</span>
                      <span className="font-medium">{subscription.plan?.name || 'Gratuit'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Annonces</span>
                      <span className="font-medium">
                        {subscription.limits?.current_products || 0} / {subscription.limits?.max_products === -1 ? '∞' : subscription.limits?.max_products || 5}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div 
                        className="bg-[#635BFF] h-2 rounded-full" 
                        style={{ 
                          width: `${subscription.limits?.max_products === -1 ? 10 : Math.min(100, (subscription.limits?.current_products / subscription.limits?.max_products) * 100)}%` 
                        }}
                      />
                    </div>
                  </div>
                  <Link to="/tarifs">
                    <Button variant="outline" size="sm" className="w-full mt-4">
                      Changer de plan
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Dashboard Tab */}
            {activeTab === 'dashboard' && (
              <div className="space-y-6">
                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="p-4">
                      <p className="text-sm text-gray-500">Total annonces</p>
                      <p className="text-2xl font-bold text-[#0A2540]">{stats?.total_annonces || 0}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-l-4 border-l-green-500">
                    <CardContent className="p-4">
                      <p className="text-sm text-gray-500">En ligne</p>
                      <p className="text-2xl font-bold text-green-600">{stats?.en_ligne || 0}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-l-4 border-l-yellow-500">
                    <CardContent className="p-4">
                      <p className="text-sm text-gray-500">En attente</p>
                      <p className="text-2xl font-bold text-yellow-600">{stats?.en_attente || 0}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-l-4 border-l-blue-500">
                    <CardContent className="p-4">
                      <p className="text-sm text-gray-500">Vues totales</p>
                      <p className="text-2xl font-bold text-blue-600">{stats?.vues_total || 0}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Quick Actions */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Actions rapides</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Link to="/user/add-product">
                        <Button className="w-full bg-[#635BFF] hover:bg-[#635BFF]/90">
                          <Plus className="w-4 h-4 mr-2" />
                          Publier une annonce
                        </Button>
                      </Link>
                      <Link to="/vendre">
                        <Button variant="outline" className="w-full">
                          Confier à GEOTS
                        </Button>
                      </Link>
                      <Link to="/catalogue">
                        <Button variant="outline" className="w-full">
                          Parcourir le catalogue
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>

                {/* Recent Products */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg">Mes dernières annonces</CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => setActiveTab('annonces')}>
                      Voir tout <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {products.length === 0 ? (
                      <div className="text-center py-8">
                        <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">Vous n'avez pas encore d'annonce</p>
                        <Link to="/user/add-product">
                          <Button className="mt-4 bg-[#635BFF]">Créer ma première annonce</Button>
                        </Link>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {products.slice(0, 3).map((product) => {
                          const statusBadge = getStatusBadge(product);
                          const StatusIcon = statusBadge.icon;
                          return (
                            <div key={product.id} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                              <div className="w-16 h-16 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                                {product.images?.[0] ? (
                                  <img src={product.images[0]} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <Package className="w-6 h-6 text-gray-400" />
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-[#0A2540] truncate">{product.name_fr || product.name}</h4>
                                <p className="text-sm text-gray-500">{product.location}</p>
                              </div>
                              <Badge className={statusBadge.color}>
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {statusBadge.label}
                              </Badge>
                              <Link to={`/user/edit-product/${product.id}`}>
                                <Button variant="ghost" size="sm">
                                  <Edit className="w-4 h-4" />
                                </Button>
                              </Link>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Annonces Tab */}
            {activeTab === 'annonces' && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Mes annonces</CardTitle>
                  <Link to="/user/add-product">
                    <Button className="bg-[#635BFF]">
                      <Plus className="w-4 h-4 mr-2" />
                      Nouvelle annonce
                    </Button>
                  </Link>
                </CardHeader>
                <CardContent>
                  {products.length === 0 ? (
                    <div className="text-center py-12">
                      <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-[#0A2540] mb-2">Aucune annonce</h3>
                      <p className="text-gray-500 mb-4">Commencez par créer votre première annonce</p>
                      <Link to="/user/add-product">
                        <Button className="bg-[#635BFF]">Créer une annonce</Button>
                      </Link>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {products.map((product) => {
                        const statusBadge = getStatusBadge(product);
                        const StatusIcon = statusBadge.icon;
                        return (
                          <div key={product.id} className="flex items-center gap-4 p-4 border rounded-lg hover:shadow-md transition-shadow">
                            <div className="w-20 h-20 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                              {product.images?.[0] ? (
                                <img src={product.images[0]} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Package className="w-8 h-8 text-gray-400" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-semibold text-[#0A2540] truncate">{product.name_fr || product.name}</h4>
                                <Badge className={statusBadge.color}>
                                  <StatusIcon className="w-3 h-3 mr-1" />
                                  {statusBadge.label}
                                </Badge>
                              </div>
                              <p className="text-sm text-gray-500 flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {product.location || 'Non précisé'}
                              </p>
                              <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Eye className="w-4 h-4" />
                                  {product.views || 0} vues
                                </span>
                                <span className="flex items-center gap-1">
                                  <MessageSquare className="w-4 h-4" />
                                  {product.contact_count || 0} contacts
                                </span>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-bold text-[#635BFF]">
                                {product.price_used || product.price_new ? 
                                  `${(product.price_used || product.price_new).toLocaleString()} €` : 
                                  'Sur demande'
                                }
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <Link to={`/product/${product.id}`}>
                                <Button variant="ghost" size="sm" title="Voir">
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </Link>
                              <Link to={`/user/edit-product/${product.id}`}>
                                <Button variant="ghost" size="sm" title="Modifier">
                                  <Edit className="w-4 h-4" />
                                </Button>
                              </Link>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Other tabs - Placeholder */}
            {activeTab === 'favoris' && (
              <Card>
                <CardHeader>
                  <CardTitle>Mes favoris</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-12">
                    <Heart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Vous n'avez pas encore de favoris</p>
                    <Link to="/catalogue">
                      <Button className="mt-4" variant="outline">Parcourir le catalogue</Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            )}

            {activeTab === 'messages' && (
              <Card>
                <CardHeader>
                  <CardTitle>Messages</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-12">
                    <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Aucun message pour le moment</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {activeTab === 'notifications' && (
              <Card>
                <CardHeader>
                  <CardTitle>Notifications</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-12">
                    <Bell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Aucune notification</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {activeTab === 'parametres' && (
              <Card>
                <CardHeader>
                  <CardTitle>Paramètres du compte</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <h3 className="font-medium text-[#0A2540] mb-4">Informations personnelles</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-500 mb-1">Nom</label>
                        <Input defaultValue={user.name || ''} />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-500 mb-1">Email</label>
                        <Input defaultValue={user.email || ''} disabled />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-500 mb-1">Téléphone</label>
                        <Input defaultValue={user.phone || ''} />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-500 mb-1">Entreprise</label>
                        <Input defaultValue={user.company_name || ''} />
                      </div>
                    </div>
                    <Button className="mt-4 bg-[#635BFF]">Sauvegarder</Button>
                  </div>

                  <div className="border-t pt-6">
                    <h3 className="font-medium text-[#0A2540] mb-4">Sécurité</h3>
                    <Button variant="outline">Changer le mot de passe</Button>
                  </div>

                  <div className="border-t pt-6">
                    <h3 className="font-medium text-red-600 mb-4">Zone dangereuse</h3>
                    <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-50">
                      Supprimer mon compte
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
